<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { RouterLink, RouterView, useRoute } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useUnreadStore } from '@/stores/unread'
import { getTelegramWebApp, initTelegramWebAppChrome, isTelegramWebApp } from '@/telegram/webapp'

const route = useRoute()
const user = useUserStore()
const unread = useUnreadStore()
const mobileOpen = ref(false)
const tgWebApp = isTelegramWebApp()

const isGuest = computed(() => route.meta.guest === true)
const isCheckout = computed(() => route.name === 'buy-product')

const nav = [
  { to: '/', label: 'Мои ключи', icon: 'bi-key' },
  { to: '/activate', label: 'Активировать код', icon: 'bi-ticket-perforated' },
  { to: '/support', label: 'Поддержка', icon: 'bi-chat-dots', badge: 'support' as const },
  { to: '/complaint', label: 'Пожаловаться на сайт', icon: 'bi-exclamation-triangle' },
  { to: '/apps', label: 'Приложения', icon: 'bi-download' },
  { to: '/news', label: 'Новости', icon: 'bi-newspaper', badge: 'news' as const },
  { to: '/settings', label: 'Настройки', icon: 'bi-gear' },
]

function badgeCount(kind: 'support' | 'news'): number {
  return kind === 'support' ? unread.supportUnread : unread.newsUnread
}

watch(isGuest, (guest) => {
  if (guest) {
    unread.stopPolling()
  } else {
    void user.fetch()
  }
})

watch(
  () => user.profile,
  (profile) => {
    if (profile && !isGuest.value) unread.startPolling()
    else unread.stopPolling()
  },
)

onMounted(() => {
  if (tgWebApp) initTelegramWebAppChrome()
  if (!isGuest.value) void user.fetch()
})

onUnmounted(() => unread.stopPolling())

function closeMobile() {
  mobileOpen.value = false
}

async function logout(e: Event) {
  e.preventDefault()
  closeMobile()
  try {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' })
  } catch {
    /* ignore */
  }
  if (tgWebApp) {
    getTelegramWebApp()?.close()
    return
  }
  window.location.href = '/login'
}
</script>

<template>
  <div v-if="isGuest" class="min-h-full">
    <RouterView />
  </div>

  <div v-else-if="isCheckout" class="min-h-full">
    <RouterView />
  </div>

  <div v-else-if="tgWebApp" class="tg-cabinet flex min-h-[100dvh] flex-col">
    <main class="cabinet-main-tg flex min-h-0 flex-1 flex-col overflow-hidden">
      <RouterView />
    </main>
  </div>

  <div v-else class="flex min-h-full">
    <button
      type="button"
      class="sidebar-toggle md:hidden"
      aria-label="Меню"
      @click="mobileOpen = true"
    >
      <i class="bi bi-list" />
    </button>

    <div
      v-if="mobileOpen"
      class="fixed inset-0 z-40 bg-black/50 md:hidden"
      @click="closeMobile"
    />

    <aside
      class="sidebar"
      :class="mobileOpen ? 'sidebar-open' : ''"
    >
      <div class="sidebar-header">
        <h4 class="text-lg font-bold text-white">443vpn</h4>
        <small v-if="user.profile?.email" class="text-white/70">{{ user.profile.email }}</small>
        <button
          type="button"
          class="sidebar-close md:hidden"
          aria-label="Закрыть"
          @click="closeMobile"
        >
          <i class="bi bi-x-lg" />
        </button>
      </div>

      <ul class="sidebar-nav">
        <li v-for="item in nav" :key="item.to">
          <RouterLink
            :to="item.to"
            class="sidebar-link"
            active-class="sidebar-link-active"
            @click="closeMobile"
          >
            <i :class="['bi', item.icon]" />
            {{ item.label }}
            <span
              v-if="item.badge && badgeCount(item.badge) > 0"
              class="sidebar-badge"
            >
              {{ badgeCount(item.badge) }}
            </span>
          </RouterLink>
        </li>
        <li>
          <a href="/login" class="sidebar-link" @click="logout">
            <i class="bi bi-box-arrow-right" />
            Выйти
          </a>
        </li>
      </ul>
    </aside>

    <main class="cabinet-main">
      <RouterView />
    </main>
  </div>
</template>
